(() => {
  let activeController;
  const root = () => window.Shopify?.routes?.root || '/';
  const pause = (ms, signal) => new Promise((resolve, reject) => {
    const stop = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', stop); resolve(); }, ms);
    signal.addEventListener('abort', stop, { once: true });
    if (signal.aborted) stop();
  });
  async function request(url, options) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
    if (!response.ok) throw new Error('Shipping request failed');
    return response.json();
  }
  if (customElements.get('shipping-estimator')) return;
  customElements.define('shipping-estimator', class extends HTMLElement {
    connectedCallback() {
      this.messages = JSON.parse(this.querySelector('[data-shipping-messages]').textContent);
      this.form = this.querySelector('form');
      this.country = this.form.elements.country;
      this.province = this.form.elements.province;
      this.provinceWrapper = this.querySelector('[data-province-wrapper]');
      this.button = this.form.querySelector('button');
      this.status = this.querySelector('[role="status"]');
      this.list = this.querySelector('ul');
      this.country.querySelector('option[value="---"]')?.remove();
      this.onCountry = () => { this.updateProvinces(); this.reset(); };
      this.onInput = () => this.reset();
      this.onSubmit = event => { event.preventDefault(); this.calculate(); };
      this.country.addEventListener('change', this.onCountry);
      this.form.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.reset(this.messages.changed));
      }
      this.updateProvinces();
      this.hidden = false;
    }
    updateProvinces() {
      let provinces = [];
      try { provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
      this.province.replaceChildren();
      if (provinces.length) {
        const prompt = document.createElement('option');
        prompt.value = '';
        prompt.textContent = this.province.dataset.placeholder;
        this.province.append(prompt);
      }
      for (const [value, name] of provinces) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = name;
        this.province.append(option);
      }
      this.provinceWrapper.hidden = provinces.length === 0;
      this.province.disabled = provinces.length === 0;
      this.province.required = provinces.length > 0;
    }
    reset(message = '') {
      this.controller?.abort();
      this.controller = undefined;
      this.button.disabled = false;
      this.status.textContent = typeof message === 'string' ? message : '';
      this.list.replaceChildren();
      this.list.hidden = true;
    }
    async calculate() {
      if (!this.form.reportValidity() || !this.country.value || this.country.value === '---') return;
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;
      this.controller = controller;
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; controller.abort(); }, 25000);
      const signal = controller.signal;
      this.button.disabled = true;
      this.list.replaceChildren();
      this.list.hidden = true;
      this.status.textContent = this.messages.loading;
      try {
        const cart = await request(`${root()}cart.js`, { signal });
        if (!cart.item_count) { this.status.textContent = this.messages.empty; return; }
        const params = new URLSearchParams();
        params.set('shipping_address[country]', this.country.value);
        params.set('shipping_address[zip]', this.form.elements.zip.value.trim());
        if (!this.province.disabled) params.set('shipping_address[province]', this.province.value);
        const suffix = `?${params.toString()}`;
        await request(`${root()}cart/prepare_shipping_rates.json${suffix}`, { method: 'POST', signal });
        let result;
        for (let attempt = 0; attempt < 12; attempt++) {
          result = await request(`${root()}cart/async_shipping_rates.json${suffix}`, { signal });
          if (result !== null) break;
          await pause(900, signal);
        }
        if (signal.aborted) return;
        if (!result || !Array.isArray(result.shipping_rates)) throw new Error('Rates unavailable');
        if (!result.shipping_rates.length) { this.status.textContent = this.messages.unavailable; return; }
        let count = 0;
        for (const rate of result.shipping_rates) {
          const amount = typeof rate.price === 'number' || typeof rate.price === 'string' && rate.price.trim() ? Number(rate.price) : NaN;
          if (!Number.isFinite(amount) || amount < 0) continue;
          const currency = rate.currency || cart.currency || this.dataset.currency;
          if (!/^[A-Z]{3}$/.test(currency)) continue;
          const price = new Intl.NumberFormat(document.documentElement.lang || 'es', { style: 'currency', currency }).format(amount);
          const item = document.createElement('li');
          item.textContent = `${rate.presentment_name || rate.name || ''}: ${price}`;
          this.list.append(item);
          count++;
        }
        if (!count) throw new Error('No valid prices');
        this.list.hidden = false;
        this.status.textContent = this.messages.ready;
      } catch (error) {
        if (this.controller === controller && this.isConnected) {
          this.status.textContent = signal.aborted && !timedOut ? '' : this.messages.error;
        }
      } finally {
        clearTimeout(timer);
        if (this.controller === controller) this.button.disabled = false;
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      this.country?.removeEventListener('change', this.onCountry);
      this.form?.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
    }
  });
})();
